import React from 'react';
import { Field } from 'react-final-form';

import { PasswordText } from '@/auth/components/PasswordTextField';
import Button from '@/components/Button';
import { Checkbox, TextField } from '@/components/FormAdapters';
import Link from '@/components/Link';
import { normalizeSpace } from '@/utils';

import { PRIVACY_POLICY, TOU_POLICY } from '../constants';
import { useStyles } from '../styles';
import type { MissingField } from '../utils/profileCompleteness';

interface UserDetailsFormProps {
  onSubmit: () => void;
  valid: boolean;
  onBack: () => void;
  // Partial profile mode - for existing users with incomplete profile
  isExistingUser?: boolean;
  missingFields?: MissingField[];
}

const checkboxStyles = {
  checkbox: {
    paddingTop: 2,
  },
  checkboxLabel: {
    alignItems: 'flex-start',
  },
};

export function UserDetailsForm({
  onSubmit,
  valid,
  onBack,
  isExistingUser = false,
  missingFields = [],
}: UserDetailsFormProps): JSX.Element {
  const s = useStyles();

  // Helper to determine if a field should be shown
  // Show all fields for new users, only missing fields for existing users
  const shouldShowField = (field: MissingField) => {
    return !isExistingUser || missingFields.includes(field);
  };

  // Check if we need to show the name row (either first or last name is missing)
  const showNameRow = shouldShowField('firstName') || shouldShowField('lastName');

  return (
    <>
      <div className={s.formContent}>
        {/* Caps match createPendingUser's own limits (validateSMBUserFields in the api):
            100 for names and job title, 255 for company. The api counts bytes, so a
            multibyte name can still be refused below 100 characters. updatePendingUser
            checks no lengths and writes to 255-wide columns, so these caps hold for it too. */}
        {/* Name row - show if either name field is missing */}
        {showNameRow && (
          <div className={s.nameRow}>
            {shouldShowField('firstName') && (
              <Field
                component={TextField}
                name="firstName"
                label="First Name"
                variant="outlined"
                placeholder="Please provide your full first name"
                changeOnBlur={false}
                inputProps={{ maxLength: 100 }}
                format={normalizeSpace}
              />
            )}
            {shouldShowField('lastName') && (
              <Field
                component={TextField}
                name="lastName"
                label="Last Name"
                variant="outlined"
                placeholder="Please provide your full last name"
                changeOnBlur={false}
                inputProps={{ maxLength: 100 }}
                format={normalizeSpace}
              />
            )}
          </div>
        )}

        {/* Company - show if missing */}
        {shouldShowField('company') && (
          <Field
            component={TextField}
            name="company"
            label="Company Name"
            variant="outlined"
            changeOnBlur={false}
            inputProps={{ maxLength: 255 }}
          />
        )}

        {/* Job title - only for new users */}
        {!isExistingUser && (
          <Field
            component={TextField}
            name="jobTitle"
            label="Job Title"
            variant="outlined"
            changeOnBlur={false}
            inputProps={{ maxLength: 100 }}
          />
        )}

        {/* Email - always shown for new users, hidden for existing */}
        {!isExistingUser && (
          <Field
            component={TextField}
            name="email"
            label="Email"
            variant="outlined"
            placeholder="you@company.com"
            changeOnBlur={false}
          />
        )}

        {/* Password fields - only for new users */}
        {!isExistingUser && (
          <>
            <Field
              component={PasswordText}
              name="password"
              label="Password"
              variant="outlined"
              showHelp
              inputProps={{ autoComplete: 'new-password' }}
            />
            <Field
              component={PasswordText}
              name="confirmPassword"
              label="Confirm Password"
              variant="outlined"
              inputProps={{ autoComplete: 'new-password' }}
            />
          </>
        )}

        {/* Privacy/Terms checkbox - only for new users */}
        {!isExistingUser && (
          <div className={s.agreements}>
            <Field
              type="checkbox"
              component={Checkbox}
              classes={checkboxStyles}
              labelClasses={{ root: checkboxStyles.checkboxLabel }}
              FormControlProps={{ margin: 'none' }}
              name="acceptPrivacyAndTerms"
              label={
                <span style={{ fontSize: 13 }}>
                  I have read and agree to OnFrontiers{' '}
                  <Link newTab href={PRIVACY_POLICY.url}>
                    {PRIVACY_POLICY.name}
                  </Link>{' '}
                  &amp;{' '}
                  <Link newTab href={TOU_POLICY.url}>
                    {TOU_POLICY.name}
                  </Link>
                </span>
              }
            />
          </div>
        )}
      </div>
      <div className={s.footer}>
        <Button variant="text" onClick={onBack} classes={{ root: s.backButton }}>
          Back
        </Button>
        <Button
          color="teal"
          size="normal"
          onClick={onSubmit}
          disabled={!valid}
          classes={{ root: s.continueButton }}
        >
          Continue
        </Button>
      </div>
    </>
  );
}

export default UserDetailsForm;
